import React, { useEffect, useRef, useState } from "react";

export interface SortOption {
  key: string;
  label: string;
}

export interface SortValue {
  sortBy: string | null;
  sortOrder: "asc" | "desc" | null;
}

interface Props {
  onChange?: (value: SortValue) => void;
  options?: SortOption[];
  defaultSortBy?: string;
  defaultSortOrder?: "asc" | "desc";
}

const DEFAULT_OPTIONS: SortOption[] = [
  { key: "taskName", label: "Task Name" },
  { key: "installerName", label: "Installer Name" },
  { key: "jobDescription", label: "Job Description" },
  { key: "submitDate", label: "Submit Date" },
];

const SortFilterDropdown: React.FC<Props> = ({
  onChange,
  options,
  defaultSortBy,
  defaultSortOrder = "asc"
}) => {
  const opts = options?.length ? options : DEFAULT_OPTIONS;
  const [isOpen, setIsOpen] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>(defaultSortBy || null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(defaultSortOrder);
  const [label, setLabel] = useState<string>("Sort By");
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!sortBy) {
      setLabel("Sort By");
      return;
    }
    const option = opts.find((o) => o.key === sortBy);
    if (option) {
      const orderIcon = sortOrder === "asc" ? "↑" : "↓";
      setLabel(`${option.label} ${orderIcon}`);
    }
  }, [sortBy, sortOrder, opts]);

  const handleSelectSortBy = (key: string) => {
    if (sortBy === key) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
      onChange?.({ sortBy: key, sortOrder: newOrder });
    } else {
      setSortBy(key);
      setSortOrder("asc");
      onChange?.({ sortBy: key, sortOrder: "asc" });
    }
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSortBy(null);
    setSortOrder("asc");
    setLabel("Sort By");
    onChange?.({ sortBy: null, sortOrder: null });
  };

  return (
    <div ref={wrapperRef} className="dropdown position-relative d-inline-block h-100">
      <button
        className="btn btn-outline-primary dropdown-toggle bg-white d-flex align-items-center h-100"
        onClick={() => setIsOpen((s) => !s)}
        style={{ minWidth: 160 }}
      >
        <span style={{ flex: 1, textAlign: "left" }}>{label}</span>
        {sortBy && (
          <i
            className="fa-solid fa-times ms-2"
            onClick={handleClear}
            style={{ fontSize: "0.85em", cursor: "pointer" }}
          />
        )}
      </button>

      {isOpen && (
        <ul className="dropdown-menu show animate-pop-in"
          style={{
            minWidth: 200,
            right: 0,
            left: 'auto'
          }}>
          {opts.map((o) => {
            const isActive = sortBy === o.key;
            return (
              <li key={o.key}>
                <button
                  className={`dropdown-item d-flex justify-content-between align-items-center ${isActive ? "active" : ""
                    }`}
                  onClick={() => handleSelectSortBy(o.key)}
                >
                  <span>{o.label}</span>
                  {isActive && (
                    <span className="ms-2">
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SortFilterDropdown;